/**
 * Deployments API - Agent deployment management across environments.
 */

import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type { GuardrailsSettings } from '../../features/guardrails/types'
import type { PrivacyConfigValue } from '@/features/privacy/PrivacyConfigForm'

// ---------------------------------------------------------------------------
// Deployment metrics types (mirrors backend Pydantic schema)
// ---------------------------------------------------------------------------

export interface TimeSeriesPoint { date: string; value: number }
/** `value` is null on no-traffic days for ratio/latency series (backend emits every day). */
export interface TimeSeriesPointFloat { date: string; value: number | null }
export interface DistributionItem { name: string; value: number; color?: string | null }

export interface CostBreakdownRow {
  name: string
  total_tokens: number
  input_tokens: number
  output_tokens: number
  estimated_cost_usd: number
}

export interface ErrorRow {
  timestamp: number
  trace_id: string
  error_type?: string | null
  error_message?: string | null
  duration_ms?: number | null
  model?: string | null
}

export interface TraceRow {
  trace_id: string
  status: string
  duration_ms?: number | null
  total_tokens: number
  model?: string | null
  input_preview?: string | null
  timestamp: number
  error_type?: string | null
}

/** AI Workflows deployment metrics – recent node execution row */
export interface WorkflowNodeExecutionRecentRow {
  execution_id: string
  node_id?: string | null
  node_type?: string | null
  created_at_us: number
  duration_ms?: number | null
  status?: string | null
  retry_count: number
  error_message?: string | null
}

/** AI Workflows Traces tab payload */
export interface WorkflowTracePanelMetrics {
  total_nodes_executed: number
  node_error_rate: number
  avg_node_latency_ms: number
  node_retry_rate: number
  avg_llm_node_latency_ms: number
  distinct_models: number
  avg_llm_usage_latency_ms: number
  llm_usage_error_rate: number
  node_type_distribution: DistributionItem[]
  node_status_distribution: DistributionItem[]
  executions_by_model: Record<string, number>
  llm_latency_by_model: Record<string, number>
  recent_node_executions: WorkflowNodeExecutionRecentRow[]
}

export interface SessionRow {
  session_id: string
  message_count: number
  total_tokens: number
  estimated_cost_usd: number
  duration_ms?: number | null
  last_active?: number | null
}

export interface UserRow {
  user_id?: string | null
  user_name: string
  session_count: number
  message_count: number
  total_tokens: number
  estimated_cost_usd: number
}

export interface ToolRow {
  operation_name: string
  tool_type?: string | null
  call_count: number
  success_rate: number
  avg_latency_ms: number
  retry_count: number
  error_count: number
}

export interface FeedbackRow {
  timestamp: number
  message_preview?: string | null
  like_status?: string | null
  rating?: number | null
  comment?: string | null
  session_id: string
}

export interface EvalHistoryRow {
  eval_session_id: string
  framework?: string | null
  score?: number | null
  metrics?: Record<string, number> | null
  status: string
  evaluated_at?: number | null
}

export interface DeploymentInfo {
  deployment_id: string
  agent_id: string
  agent_name: string | null
  agent_version: string
  environment: string
  status: string
  deployed_at: number | null
  is_external?: boolean
}

export interface OverviewMetrics {
  total_invocations: number
  success_rate: number
  error_rate: number
  avg_latency_ms: number
  p99_latency_ms: number
  total_tokens: number
  estimated_cost_usd: number
  user_satisfaction_pct: number | null
  invocations_trend: TimeSeriesPoint[]
  status_distribution: DistributionItem[]
  latency_p50_trend: TimeSeriesPointFloat[]
  latency_p90_trend: TimeSeriesPointFloat[]
  latency_p99_trend: TimeSeriesPointFloat[]
  cost_trend: TimeSeriesPointFloat[]
}

export interface PerformanceMetrics {
  avg_duration_ms: number
  p50_duration_ms: number
  p90_duration_ms: number
  p99_duration_ms: number
  throughput_per_min: number
  avg_spans_per_trace: number
  avg_llm_calls_per_invocation: number
  time_to_first_token_ms: number | null
  duration_p50_trend: TimeSeriesPointFloat[]
  duration_p90_trend: TimeSeriesPointFloat[]
  duration_p99_trend: TimeSeriesPointFloat[]
  latency_histogram: DistributionItem[]
  throughput_trend: TimeSeriesPoint[]
  slowest_span_types: Record<string, number>
  slowest_operations: Record<string, number>
  llm_latency_trend: TimeSeriesPointFloat[]
  tool_latency_trend: TimeSeriesPointFloat[]
}

export interface CostTokenMetrics {
  total_tokens: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  input_token_discount?: number
  estimated_cost_usd: number
  llm_calls: number
  avg_tokens_per_call: number
  avg_cost_per_invocation: number
  daily_avg_cost: number
  token_efficiency_ratio: number
  intelligent_cache_hit_rate: number
  cache_tokens_saved: number
  cache_cost_saved_usd: number
  cache_api_calls_saved: number
  cache_latency_saved_ms: number
  kv_cache_hit_rate: number
  token_trend: TimeSeriesPoint[]
  input_token_trend: TimeSeriesPoint[]
  output_token_trend: TimeSeriesPoint[]
  cost_trend: TimeSeriesPointFloat[]
  cumulative_cost_trend: TimeSeriesPointFloat[]
  model_token_distribution: DistributionItem[]
  model_cost_distribution: DistributionItem[]
  tokens_by_model: Record<string, number>
  top_cost_drivers: CostBreakdownRow[]
}

export interface ErrorMetrics {
  error_count: number
  error_rate: number
  unique_error_types: number
  failed_llm_calls: number
  rate_limit_hits: number
  timeout_count: number
  avg_retries_per_trace: number
  avg_warnings_per_trace: number
  error_rate_trend: TimeSeriesPointFloat[]
  error_type_distribution: DistributionItem[]
  llm_vs_tool_errors: DistributionItem[]
  finish_reason_distribution: DistributionItem[]
  error_codes: Record<string, number>
  errors_by_type_trend: Record<string, TimeSeriesPoint[]>
  recent_errors: ErrorRow[]
}

export interface SessionUserMetrics {
  total_sessions: number
  unique_users: number
  total_messages: number
  avg_messages_per_session: number
  avg_session_duration_ms: number
  avg_latency_per_message_ms: number
  avg_tokens_per_session: number
  avg_cost_per_session: number
  avg_cost_per_user: number
  avg_cost_per_1k_records?: number
  session_satisfaction_pct: number | null
  sessions_trend: TimeSeriesPoint[]
  messages_trend: TimeSeriesPoint[]
  users_trend: TimeSeriesPoint[]
  trigger_source_trends?: Record<string, TimeSeriesPoint[]>
  tokens_per_session_trend: TimeSeriesPointFloat[]
  session_status_distribution: DistributionItem[]
  top_sessions: SessionRow[]
  top_users: UserRow[]
}

export interface TraceSpanMetrics {
  total_traces: number
  avg_spans_per_trace: number
  avg_tokens_per_trace: number
  distinct_models: number
  total_llm_spans: number
  total_tool_spans: number
  avg_llm_latency_ms: number
  llm_error_rate: number
  trace_status_distribution: DistributionItem[]
  span_type_distribution: DistributionItem[]
  traces_by_model: Record<string, number>
  llm_latency_by_model: Record<string, number>
  recent_traces: TraceRow[]
  workflow_nodes?: WorkflowTracePanelMetrics | null
}

export interface ToolRAGMetrics {
  total_tool_calls: number
  unique_tools: number
  tool_success_rate: number
  avg_tool_latency_ms: number
  hitl_required_count: number
  hitl_approval_rate: number
  avg_hitl_wait_time_ms: number
  total_retrievals: number
  avg_docs_retrieved: number
  tool_retry_rate: number
  tool_usage: Record<string, number>
  tool_latency: Record<string, number>
  tool_error_rate: Record<string, number>
  tool_calls_trend: Record<string, TimeSeriesPoint[]>
  hitl_trend: TimeSeriesPoint[]
  hitl_approved_trend: TimeSeriesPoint[]
  retrieval_strategy_distribution: DistributionItem[]
  http_status_distribution: DistributionItem[]
  tool_details: ToolRow[]
}

export interface FeedbackEvalMetrics {
  total_feedback: number
  thumbs_up: number
  thumbs_down: number
  user_satisfaction_pct: number
  avg_star_rating: number | null
  session_satisfaction_pct: number | null
  feedback_response_rate: number
  latest_eval_score: number | null
  eval_score_delta: number | null
  total_eval_runs: number
  feedback_sentiment_trend: TimeSeriesPoint[]
  feedback_dislikes_trend: TimeSeriesPoint[]
  satisfaction_trend: TimeSeriesPointFloat[]
  star_rating_distribution: DistributionItem[]
  eval_score_trend: TimeSeriesPointFloat[]
  eval_metric_breakdown: Record<string, number>
  feedback_volume_trend: TimeSeriesPoint[]
  recent_feedback: FeedbackRow[]
  eval_history: EvalHistoryRow[]
}

export interface DeploymentMetricsResponse {
  deployment: DeploymentInfo
  overview: OverviewMetrics
  performance: PerformanceMetrics
  cost_tokens: CostTokenMetrics
  errors: ErrorMetrics
  sessions_users: SessionUserMetrics
  traces_spans: TraceSpanMetrics
  tools_rag: ToolRAGMetrics
  feedback_evals: FeedbackEvalMetrics
}

// ---------------------------------------------------------------------------
// Deployment CRUD types
// ---------------------------------------------------------------------------

export type VoiceChannel = 'chat' | 'voice' | 'meeting'

export interface VoiceConfig {
  stt_model_id?: string
  tts_model_id?: string
  realtime_model_id?: string
  telephony_tool_id?: string
  meeting_tool_id?: string
  voice_id?: string
  language?: string
  interruption_policy?: 'allowed' | 'blocked' | 'hold'
  /** Seconds of silence before end-of-turn (voice gateway). */
  silence_threshold?: number
  /** @deprecated Legacy 0–1 sensitivity; use silence_threshold. */
  vad_threshold?: number
  welcome_message?: string
  personality?: string
  response_length?: 'short' | 'medium' | 'long'
  recording_enabled?: boolean
  recording_retention_days?: number
  phone_number_id?: string
  telephony_provider?: string
}

export interface DeploymentCreate {
  agent_id?: string
  external_agent_id?: string
  orchestrator_connection_id?: string
  environment: string
  checklist_responses?: Record<string, boolean>
  checklist_evidence?: Record<string, string>
  eval_summary?: Record<string, unknown>
  deployment_metadata?: Record<string, unknown>
  channels?: VoiceChannel[]
  voice_config?: VoiceConfig | null
  agent_identity_id?: string | null
}

export interface DeploymentRead {
  id: string
  agent_id?: string | null
  external_agent_id?: string | null
  agent_version: string
  environment: string
  status: string
  checklist_responses?: Record<string, boolean> | null
  eval_summary?: Record<string, unknown> | null
  deployment_metadata?: Record<string, unknown> | null
  created_at: number | null
  updated_at: number | null
  created_by: string | null
  updated_by: string | null
  creator_name?: string | null
  agent_name?: string | null
  tenant_id?: string | null
  tenant_name?: string | null
  store_listing_status?: string | null
  store_listing_id?: string | null
  channels?: VoiceChannel[] | null
  voice_config?: VoiceConfig | null
  /** Total chat sessions for this agent+environment. */
  session_count?: number
  /** Distinct users who interacted with this agent+environment. */
  active_user_count?: number
  /** native = deployments table; external = agentserve standalone */
  source?: 'native' | 'external'
  platform?: string | null
  orchestrator_connection_id?: string | null
  /** Whether the current user has favourited this deployment. */
  is_fav?: boolean
}

export interface DeploymentList {
  items: DeploymentRead[]
  total: number
}

export interface ChecklistItem {
  key: string
  label: string
  required: boolean
  requires_evidence?: boolean
  evidence_type?: string | null
  evidence_label?: string | null
}

export interface DeploymentChecklistConfig {
  checklist_items: ChecklistItem[]
}

export interface DeploymentArchivePrerequisitesRead {
  deployment_id: string
  has_active_store_listing: boolean
  active_listing_ids: string[]
}

export interface BulkDeploymentOperationResult {
  deleted: number
  skipped: number
  errors: string[]
}

export interface DeploymentModelOverrides {
  node_overrides: Record<string, string>
}

export type AgentKnowledgeOverrideMode = 'inherit' | 'override' | 'none'

/** Deployment overlay for graph_definition.knowledge_settings.knowledge. */
export interface AgentKnowledgeOverride {
  mode: AgentKnowledgeOverrideMode
  /** Used when mode is override. Empty when mode is inherit or none. */
  knowledge: string[]
}

export interface DeploymentKnowledgeOverrides {
  node_overrides: Record<string, string[]>
  /** Omit on write to leave the stored agent-level overlay unchanged. */
  agent_override?: AgentKnowledgeOverride | null
}

export interface DeploymentChatUiSettings {
  show_thinking_tokens: boolean
  show_execution_timeline: boolean
  show_node_traces: boolean
  show_tool_calls: boolean
}

export const DEFAULT_DEPLOYMENT_CHAT_UI_SETTINGS: DeploymentChatUiSettings = {
  show_thinking_tokens: true,
  show_execution_timeline: true,
  show_node_traces: true,
  show_tool_calls: true,
}

/** Merge partial/listing settings with defaults (missing keys stay visible). */
export function resolveDeploymentChatUiSettings(
  settings?: Partial<DeploymentChatUiSettings> | null,
): DeploymentChatUiSettings {
  const s = (settings ?? null) as
    | (Partial<DeploymentChatUiSettings> & {
        showThinkingTokens?: boolean
        showExecutionTimeline?: boolean
        showNodeTraces?: boolean
        showToolCalls?: boolean
      })
    | null
  return {
    show_thinking_tokens:
      s?.show_thinking_tokens ??
      s?.showThinkingTokens ??
      DEFAULT_DEPLOYMENT_CHAT_UI_SETTINGS.show_thinking_tokens,
    show_execution_timeline:
      s?.show_execution_timeline ??
      s?.showExecutionTimeline ??
      DEFAULT_DEPLOYMENT_CHAT_UI_SETTINGS.show_execution_timeline,
    show_node_traces:
      s?.show_node_traces ??
      s?.showNodeTraces ??
      DEFAULT_DEPLOYMENT_CHAT_UI_SETTINGS.show_node_traces,
    show_tool_calls:
      s?.show_tool_calls ??
      s?.showToolCalls ??
      DEFAULT_DEPLOYMENT_CHAT_UI_SETTINGS.show_tool_calls,
  }
}

export interface DeploymentSettings {
  cache: Record<string, unknown>
  memory: Record<string, unknown>
  model_overrides: DeploymentModelOverrides
  knowledge_overrides: DeploymentKnowledgeOverrides
  guardrails: GuardrailsSettings
  chat_ui: DeploymentChatUiSettings
  privacy?: PrivacyConfigValue
  privacy_overlay_present?: boolean
}

export interface DeploymentSettingsUpdate {
  cache?: Record<string, unknown>
  memory?: Record<string, unknown>
  model_overrides?: DeploymentModelOverrides
  knowledge_overrides?: DeploymentKnowledgeOverrides
  guardrails?: GuardrailsSettings
  chat_ui?: DeploymentChatUiSettings
  privacy?: PrivacyConfigValue
  privacy_reset?: boolean
}

export interface DatabricksAppsHopRead {
  name: string
  status: string
  started_at?: number | null
  ended_at?: number | null
  duration_ms?: number | null
  error?: string | null
  error_detail?: string | null
}

/** GET /deployments/{id}/databricks/apps/status */
export interface DatabricksAppsDeployStatus {
  deployment_id: string
  status: string
  hop?: string | null
  app_name?: string | null
  app_url?: string | null
  git_commit?: string | null
  git_url?: string | null
  git_commit_url?: string | null
  source_code_path?: string | null
  databricks_deployment_id?: string | null
  checksum?: string | null
  error?: string | null
  error_detail?: string | null
  linked_external_agent_id?: string | null
  workspace_url?: string | null
  apps_console_url?: string | null
  hops?: DatabricksAppsHopRead[]
}

export interface SnowflakeCortexHopRead {
  name: string
  status: string
  started_at?: number | null
  ended_at?: number | null
  duration_ms?: number | null
  error?: string | null
  error_detail?: string | null
}

/** GET /deployments/{id}/snowflake/cortex/status */
export interface SnowflakeCortexDeployStatus {
  deployment_id: string
  status: string
  hop?: string | null
  agent_name?: string | null
  service_url?: string | null
  account_url?: string | null
  checksum?: string | null
  error?: string | null
  error_detail?: string | null
  linked_external_agent_id?: string | null
  deploy_mode?: string | null
  hops?: SnowflakeCortexHopRead[]
}

export interface GoogleAgentEngineHopRead {
  name: string
  status: string
  started_at?: number | null
  ended_at?: number | null
  duration_ms?: number | null
  error?: string | null
  error_detail?: string | null
}

/** GET /deployments/{id}/google/agent-engine/status */
export interface GoogleAgentEngineDeployStatus {
  deployment_id: string
  status: string
  hop?: string | null
  engine_name?: string | null
  reasoning_engine_id?: string | null
  query_url?: string | null
  location?: string | null
  checksum?: string | null
  error?: string | null
  error_detail?: string | null
  linked_external_agent_id?: string | null
  hops?: GoogleAgentEngineHopRead[]
}

/** Pack from GET /deployments/{id}/copilot-studio/connected-agent */
export interface CopilotStudioConnectedAgentPack {
  deployment_id: string
  agent_name: string
  checklist: string[]
  openapi: Record<string, unknown>
  topic_yaml_stub: string
  chat_url: string
}

export const deploymentsApi = {
  list: (params?: {
    environment?: string
    project_id?: string
    agent_id?: string
    status?: string
    search?: string
    created_by?: string
    /** Scheduled-monitor bucket (UAT/PROD only); see MonitorStatusBucket. */
    monitoring_status?: string
    page?: number
    page_size?: number
    scope?: 'all'
    is_favourite?: boolean
  }) => {
    const query = buildQueryString(params || {})
    return request<DeploymentList>(`/deployments${query}`)
  },

  get: (id: string) => request<DeploymentRead>(`/deployments/${id}`),

  create: (data: DeploymentCreate) =>
    request<DeploymentRead>('/deployments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  databricksAppsStatus: (id: string) =>
    request<DatabricksAppsDeployStatus>(`/deployments/${id}/databricks/apps/status`),

  databricksAppsRetry: (id: string, options?: { tenantId?: string }) =>
    request<DeploymentRead>(`/deployments/${id}/databricks/apps/retry`, {
      method: 'POST',
      tenantId: options?.tenantId,
    }),

  snowflakeCortexStatus: (id: string) =>
    request<SnowflakeCortexDeployStatus>(`/deployments/${id}/snowflake/cortex/status`),

  snowflakeCortexRetry: (id: string, options?: { tenantId?: string }) =>
    request<DeploymentRead>(`/deployments/${id}/snowflake/cortex/retry`, {
      method: 'POST',
      tenantId: options?.tenantId,
    }),

  googleAgentEngineStatus: (id: string) =>
    request<GoogleAgentEngineDeployStatus>(`/deployments/${id}/google/agent-engine/status`),

  googleAgentEngineRetry: (id: string, options?: { tenantId?: string }) =>
    request<DeploymentRead>(`/deployments/${id}/google/agent-engine/retry`, {
      method: 'POST',
      tenantId: options?.tenantId,
    }),

  /** Get or create a TEST deployment for voice telephony (Agent Studio publish / campaigns). */
  getOrCreateVoiceTestDeployment: (agentId: string) =>
    request<DeploymentRead>('/deployments/voice-test', {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId }),
    }),

  archive: (id: string, options?: { tenantId?: string }) =>
    request<DeploymentRead>(`/deployments/${id}/archive`, {
      method: 'DELETE',
      tenantId: options?.tenantId,
    }),

  getArchivePrerequisites: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<DeploymentArchivePrerequisitesRead>(`/deployments/${id}/archive-prerequisites`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  redeploy: (id: string, options?: { tenantId?: string }) =>
    request<DeploymentRead>(`/deployments/${id}/redeploy`, {
      method: 'POST',
      tenantId: options?.tenantId,
    }),

  bulkArchive: (deploymentIds: string[], options?: { tenantId?: string }) =>
    request<BulkDeploymentOperationResult>('/deployments/bulk-archive', {
      method: 'POST',
      body: JSON.stringify({ deployment_ids: deploymentIds }),
      tenantId: options?.tenantId,
    }),

  bulkRedeploy: (deploymentIds: string[], options?: { tenantId?: string }) =>
    request<BulkDeploymentOperationResult>('/deployments/bulk-redeploy', {
      method: 'POST',
      body: JSON.stringify({ deployment_ids: deploymentIds }),
      tenantId: options?.tenantId,
    }),

  getChecklist: () =>
    request<DeploymentChecklistConfig>('/deployments/checklist'),

  getEvalSummary: (id: string) =>
    request<Record<string, unknown>>(`/deployments/${id}/eval-summary`),

  getMetrics: (
    id: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ) => {
    const sp = new URLSearchParams()
    if (params?.period) sp.set('period', params.period)
    if (params?.start_date) sp.set('start_date', params.start_date)
    if (params?.end_date) sp.set('end_date', params.end_date)
    const q = sp.toString() ? `?${sp.toString()}` : ''
    return request<DeploymentMetricsResponse>(`/deployments/${id}/metrics${q}`)
  },

  getSettings: (id: string) =>
    request<DeploymentSettings>(`/deployments/${id}/settings`),

  updateSettings: (id: string, data: DeploymentSettingsUpdate) =>
    request<DeploymentSettings>(`/deployments/${id}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getGuardrailsSettings: (id: string) =>
    request<import('../../features/guardrails/types').GuardrailsSettings>(
      `/deployments/${id}/guardrails-settings`,
    ),

  updateGuardrailsSettings: (
    id: string,
    settings: import('../../features/guardrails/types').GuardrailsSettings,
  ) =>
    request<import('../../features/guardrails/types').GuardrailsSettings>(
      `/deployments/${id}/guardrails-settings`,
      { method: 'PUT', body: JSON.stringify(settings) },
    ),

  getModelOverrides: (id: string) =>
    request<{ node_overrides: Record<string, string> }>(`/deployments/${id}/model-overrides`),

  updateModelOverrides: (id: string, nodeOverrides: Record<string, string>) =>
    request<{ node_overrides: Record<string, string> }>(`/deployments/${id}/model-overrides`, {
      method: 'PUT',
      body: JSON.stringify({ node_overrides: nodeOverrides }),
    }),

  getKnowledgeOverrides: (id: string) =>
    request<DeploymentKnowledgeOverrides>(`/deployments/${id}/knowledge-overrides`),

  updateKnowledgeOverrides: (id: string, payload: DeploymentKnowledgeOverrides) =>
    request<DeploymentKnowledgeOverrides>(`/deployments/${id}/knowledge-overrides`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  getPrivacySettings: (id: string) =>
    request<{ privacy: PrivacyConfigValue; privacy_overlay_present: boolean }>(
      `/deployments/${id}/privacy-settings`,
    ),

  updatePrivacySettings: (
    id: string,
    data: { privacy?: PrivacyConfigValue; privacy_reset?: boolean },
  ) =>
    request<{ privacy: PrivacyConfigValue; privacy_overlay_present: boolean }>(
      `/deployments/${id}/privacy-settings`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),

  /** OpenAPI + topic stub to expose this deployment as a Copilot Studio Connected Agent. */
  getCopilotStudioConnectedAgent: (id: string, options?: { tenantId?: string }) =>
    request<CopilotStudioConnectedAgentPack>(
      `/deployments/${id}/copilot-studio/connected-agent`,
      { tenantId: options?.tenantId },
    ),

}
